// src/pages/admin/AdpNetsuite.jsx
import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import {
  Card,
  Button,
  ConfirmModal,
  Loading,
  usePersistedState,
  showSuccess,
  showError,
  PayPeriod,
} from "@common";
import {
  useCreateNetsuiteJournalEntry,
  useSearchNetsuiteJournalEntriesByMonthYear,
  usePractitionerProduction,
} from "@api/hooks";
import { adpNetsuiteColumns, ADP_NETSUITE_EMPTY_LINES_TEXT } from "@utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import ADPNetsuiteSubmitFormPreview from "./ADPNetsuiteSubmitFormPreview";
import ADPNetsuiteBulkSubmitFormPreview from "./ADPNetsuiteBulkSubmitFormPreview";

// ─── CSV parser ───────────────────────────────────────────────────────────────

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];

  const parseLine = (line) => {
    const fields = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === "," && !inQuotes) {
        fields.push(cur.trim());
        cur = "";
      } else {
        cur += ch;
      }
    }
    fields.push(cur.trim());
    return fields;
  };

  const headers = parseLine(lines[0]);
  return lines.slice(1).map((line) => {
    const values = parseLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h] = values[i] ?? "";
    });
    return row;
  });
}

// ─── Grouping ─────────────────────────────────────────────────────────────────

function groupByAdpCo(rows) {
  const ADP_CO_KEY =
    rows.length > 0
      ? Object.keys(rows[0]).find((k) => k.trim().toUpperCase() === "ADP CO") || "ADP CO"
      : "ADP CO";

  const groups = {};
  for (const row of rows) {
    const key = row[ADP_CO_KEY] ?? "(blank)";
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  }
  return { groups, ADP_CO_KEY };
}

// Stable unique id for a journal entry, used as the collapse/expand key.
function getEntryUid(entry, fallback) {
  return String(entry?.id ?? entry?.transactionnumber ?? fallback);
}

// ── Totals helpers ──────────────────────────────────────────────────────────

const toAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatTotalAmount = (n) =>
  toAmount(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Sum debit / credit across a set of transaction lines.
function computeLineTotals(lineItems) {
  let debit = 0;
  let credit = 0;
  for (const line of lineItems || []) {
    debit += toAmount(line?.debitforeignamount);
    credit += toAmount(line?.creditforeignamount);
  }
  return { debit, credit };
}

// Sum debit / credit across every entry in a day group.
function computeDayTotals(entries) {
  let debit = 0;
  let credit = 0;
  for (const entry of entries || []) {
    const lines = Array.isArray(entry?.transaction_lines) ? entry.transaction_lines : [];
    const t = computeLineTotals(lines);
    debit += t.debit;
    credit += t.credit;
  }
  return { debit, credit };
}

function JournalEntryLinesTable({ entry, entryKey, lineItems, viewColumns }) {
  const totals = computeLineTotals(lineItems);
  return (
    <div className="overflow-x-auto rounded-b-md">
      <table className="w-full table-fixed divide-y divide-palette-gray-divider text-[13px]">
        <colgroup>
          {viewColumns.map((col) => (
            <col key={`col-${col.key}`} style={{ width: col.width || undefined }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {viewColumns.map((col) => (
              <th key={col.key} className={col.headerClassName}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-palette-gray-subtle">
          {lineItems.length > 0 ? (
            lineItems.map((line, lineIdx) => (
              <tr
                key={`${entryKey}-line-${lineIdx}`}
                className="transition-colors hover:brightness-95"
              >
                {viewColumns.map((col) => (
                  <td key={`${entryKey}-line-${lineIdx}-${col.key}`} className={col.className}>
                    {col.render ? col.render(line, entry) : line[col.key]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={viewColumns.length}
                className="px-3 py-3 text-sm text-palette-gray-secondary"
              >
                {ADP_NETSUITE_EMPTY_LINES_TEXT}
              </td>
            </tr>
          )}
        </tbody>
        {lineItems.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-palette-gray-border bg-palette-gray-soft font-bold">
              {viewColumns.map((col) => {
                if (col.key === "account") {
                  return (
                    <td
                      key={`${entryKey}-total-${col.key}`}
                      className="px-3 py-2 text-left text-[13px] uppercase tracking-wide text-palette-gray-heading"
                    >
                      Column Total
                    </td>
                  );
                }
                if (col.key === "debit" || col.key === "credit") {
                  return (
                    <td
                      key={`${entryKey}-total-${col.key}`}
                      className="px-3 py-2 text-right text-[13px] tabular-nums text-palette-gray-primary"
                    >
                      {formatTotalAmount(col.key === "debit" ? totals.debit : totals.credit)}
                    </td>
                  );
                }
                return <td key={`${entryKey}-total-${col.key}`} className="px-3 py-2" />;
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function JournalEntryDateGroup({
  dateKey,
  entries,
  viewColumns,
  isEntryExpanded,
  onToggleEntry,
  productionBySubsidiary,
  productionLoading,
}) {
  return (
    <div className="rounded-lg border border-palette-gray-divider bg-white">
      <div className="overflow-x-auto">
        <div className="space-y-3 p-3">
          {entries.map((entry, index) => {
            const lineItems = Array.isArray(entry.transaction_lines) ? entry.transaction_lines : [];
            const entryKey = getEntryUid(entry, `${dateKey}-${index}`);
            const subsidiaryName = entry.subsidiary_name || "Unknown Subsidiary";
            const expanded = isEntryExpanded(entryKey);
            const entryTotals = computeLineTotals(lineItems);
            const subsidiarySid = entry.subsidiary_id != null ? Number(entry.subsidiary_id) : null;
            const production =
              subsidiarySid != null && productionBySubsidiary
                ? productionBySubsidiary[subsidiarySid]
                : undefined;
            return (
              <div
                key={entryKey}
                className="overflow-hidden rounded-md border border-palette-gray-divider bg-white"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-palette-gray-divider bg-palette-gray-soft px-3 py-2">
                  <button
                    type="button"
                    onClick={() => onToggleEntry(entryKey)}
                    className="flex flex-1 items-center gap-2 text-left"
                    aria-expanded={expanded}
                    title={expanded ? "Collapse table" : "Expand table"}
                  >
                    <span className="text-palette-gray-secondary transition-transform">
                      {expanded ? "▾" : "▸"}
                    </span>
                    <span className="text-sm font-semibold text-palette-gray-text">
                      {subsidiaryName}
                    </span>
                    <span className="text-xs text-palette-gray-placeholder">
                      {lineItems.length} line{lineItems.length === 1 ? "" : "s"}
                    </span>
                  </button>
                  <div className="flex items-center gap-4 text-xs">
                    {productionLoading ? (
                      <span className="inline-flex items-center gap-1 text-palette-violet-label italic">
                        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          />
                        </svg>
                        Production…
                      </span>
                    ) : production != null ? (
                      <span className="inline-flex flex-col items-end gap-0">
                        {Number(production) > 0 && (
                          <span className="font-bold tabular-nums text-palette-amber-badge">
                            {((entryTotals.debit / Number(production)) * 100).toFixed(1)}%
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 font-bold text-palette-violet-heading">
                          <span className="uppercase tracking-wide text-palette-violet-label">
                            Production
                          </span>
                          <span className="tabular-nums">
                            {Number(production).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </span>
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1 font-bold text-palette-emerald-hover">
                      <span className="uppercase tracking-wide text-palette-emerald-badge">
                        Debit
                      </span>
                      <span className="tabular-nums">{formatTotalAmount(entryTotals.debit)}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 font-bold text-palette-rose-heading">
                      <span className="uppercase tracking-wide text-palette-rose-hover">
                        Credit
                      </span>
                      <span className="tabular-nums">{formatTotalAmount(entryTotals.credit)}</span>
                    </span>
                    {entry.link ? (
                      <a
                        href={entry.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border border-palette-emerald-ring bg-palette-emerald-muted px-2.5 py-1 text-xs font-semibold text-palette-emerald-text shadow-sm ring-1 ring-palette-emerald-border hover:bg-palette-emerald-border hover:underline"
                        title="Open in NetSuite"
                      >
                        <svg
                          className="h-3.5 w-3.5 text-palette-emerald-badge"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {entry.transactionnumber || `#${entry.id}`}
                      </a>
                    ) : (
                      <span className="font-semibold text-palette-gray-badge">
                        {entry.transactionnumber || `#${entry.id || "-"}`}
                      </span>
                    )}
                  </div>
                </div>

                {expanded && (
                  <JournalEntryLinesTable
                    entry={entry}
                    entryKey={entryKey}
                    lineItems={lineItems}
                    viewColumns={viewColumns}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function JournalEntriesViewCard({
  month,
  journalEntriesQuery,
  journalEntriesLoading,
  journalEntries,
  journalEntriesByDate,
  viewColumns,
  productionBySubsidiary,
  productionLoading,
}) {
  // expandedEntries: map of entryKey -> true (expanded). Default = collapsed.
  const [expandedEntries, setExpandedEntries] = usePersistedState(
    "adpNetsuite:expandedEntries",
    {},
  );

  const allEntryKeys = useMemo(() => {
    const keys = [];
    for (const [dateKey, entries] of journalEntriesByDate) {
      entries.forEach((entry, index) => keys.push(getEntryUid(entry, `${dateKey}-${index}`)));
    }
    return keys;
  }, [journalEntriesByDate]);

  const isEntryExpanded = useCallback((key) => !!expandedEntries[key], [expandedEntries]);

  const toggleEntry = useCallback(
    (key) => {
      setExpandedEntries((prev) => {
        const next = { ...prev };
        if (next[key]) delete next[key];
        else next[key] = true;
        return next;
      });
    },
    [setExpandedEntries],
  );

  const allExpanded = allEntryKeys.length > 0 && allEntryKeys.every((k) => !!expandedEntries[k]);

  const handleToggleAll = useCallback(() => {
    if (allExpanded) {
      setExpandedEntries({});
    } else {
      setExpandedEntries(Object.fromEntries(allEntryKeys.map((k) => [k, true])));
    }
  }, [allExpanded, allEntryKeys, setExpandedEntries]);

  const monthTotals = useMemo(() => computeDayTotals(journalEntries), [journalEntries]);

  const showToolbar = !!month && !journalEntriesLoading && journalEntries.length > 0;

  return (
    <Card>
      <div className="flex items-center justify-between gap-2 pb-2 border-b border-palette-gray-divider">
        <div>
          <div className="text-sm font-semibold text-palette-gray-text">
            Journal Entries by Month
          </div>
          <div className="text-xs text-palette-gray-secondary">
            {month
              ? `Selected month: ${month}${journalEntries.length > 0 ? ` · ${journalEntries.length} Journal ${journalEntries.length === 1 ? "Entry" : "Entries"}` : ""}`
              : "Select a month to load journal entries."}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showToolbar && (
            <>
              <Button variant="secondary" size="sm" onClick={handleToggleAll}>
                {allExpanded ? "Collapse All" : "Expand All"}
              </Button>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-palette-emerald-hover">
                <span className="uppercase tracking-wide text-palette-emerald-badge">
                  Period Debit
                </span>
                <span className="tabular-nums">{formatTotalAmount(monthTotals.debit)}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-palette-rose-heading">
                <span className="uppercase tracking-wide text-palette-rose-hover">
                  Period Credit
                </span>
                <span className="tabular-nums">{formatTotalAmount(monthTotals.credit)}</span>
              </span>
            </>
          )}
          {journalEntriesQuery.isError ? (
            <span className="text-xs text-palette-red-label">Failed to load</span>
          ) : null}
        </div>
      </div>

      {month ? (
        journalEntriesLoading ? (
          <div className="mt-3 rounded-md border border-dashed border-palette-gray-divider bg-palette-gray-soft px-4 py-5">
            <Loading text="Loading journal entries for the selected month..." size="sm" />
          </div>
        ) : journalEntries.length > 0 ? (
          <div className="mt-3 space-y-3">
            {journalEntriesByDate.map(([dateKey, entries]) => (
              <JournalEntryDateGroup
                key={dateKey}
                dateKey={dateKey}
                entries={entries}
                viewColumns={viewColumns}
                isEntryExpanded={isEntryExpanded}
                onToggleEntry={toggleEntry}
                productionBySubsidiary={productionBySubsidiary}
                productionLoading={productionLoading}
              />
            ))}
          </div>
        ) : (
          <div className="mt-3 text-sm text-palette-gray-secondary">
            No journal entries found for this month.
          </div>
        )
      ) : null}
    </Card>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdpNetsuite({ month = "", payPeriod } = {}) {
  // Persist CSV data across tab switches via sessionStorage
  const [rows, setRows] = usePersistedState("adpNetsuite:rows", null);
  const [fileName, setFileName] = usePersistedState("adpNetsuite:fileName", "");

  const [error, setError] = useState("");
  // expandedGroups: undefined key = expanded (default), false = collapsed
  const [expandedGroups, setExpandedGroups] = useState({});

  // Upload tab's own period selection — independent from the View tab's month prop
  const [uploadPeriod, setUploadPeriod] = useState(null);

  // Journal entry modal state
  const [journalPreview, setJournalPreview] = useState(null); // { adpCo, rows }
  const payloadRef = useRef(null); // latest payload from preview component
  const [singleJEReady, setSingleJEReady] = useState(false);
  const [justCreated, setJustCreated] = useState(new Set());

  // Bulk journal entry modal state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPayloads, setBulkPayloads] = useState({}); // { [adpCo]: payload | null }
  const [bulkResults, setBulkResults] = useState({}); // { [adpCo]: { ok, error? } }
  const [bulkSubmitting, setBulkSubmitting] = useState(() => new Set());
  const [bulkInProgress, setBulkInProgress] = useState(false);

  const createJournalMut = useCreateNetsuiteJournalEntry();
  const journalEntriesQuery = useSearchNetsuiteJournalEntriesByMonthYear(
    { month },
    {
      enabled: !!month,
      refetchOnWindowFocus: false,
      // Keep data fresh enough while avoiding re-fetch-on-every-mount flashes.
      staleTime: 5 * 60 * 1000,
    },
  );

  const inputRef = useRef(null);

  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [rows]);

  // Derive grouped data from persisted rows
  const { groups: grouped, ADP_CO_KEY: adpCoKey } = useMemo(() => {
    if (!rows || rows.length === 0) return { groups: null, ADP_CO_KEY: "ADP CO" };
    return groupByAdpCo(rows);
  }, [rows]);

  const columns = useMemo(() => (rows && rows.length > 0 ? Object.keys(rows[0]) : []), [rows]);
  const viewColumns = useMemo(() => adpNetsuiteColumns(), []);
  const uploadColumnWidths = useMemo(() => {
    if (!rows || rows.length === 0 || columns.length === 0) return {};

    const sampleRows = rows.slice(0, 200);
    const rawWidths = {};

    for (const col of columns) {
      let maxLen = String(col || "").length;
      for (const row of sampleRows) {
        const value = row?.[col];
        if (value === null || value === undefined) continue;
        maxLen = Math.max(maxLen, String(value).length);
      }

      const lower = String(col).toLowerCase();
      if (lower.includes("memo") || lower.includes("description") || lower.includes("name")) {
        maxLen += 8;
      }

      rawWidths[col] = Math.min(360, Math.max(120, maxLen * 8 + 24));
    }

    // Convert estimated pixel widths into proportional percentages so the
    // table always spans the full container width regardless of column count.
    const total = columns.reduce((sum, col) => sum + (rawWidths[col] || 0), 0) || 1;
    const widths = {};
    for (const col of columns) {
      widths[col] = `${((rawWidths[col] || 0) / total) * 100}%`;
    }

    return widths;
  }, [rows, columns]);

  // ── File handling ──────────────────────────────────────────────────────────

  function handleFile(file) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setError("Please upload a .csv file.");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCSV(e.target.result);
        if (parsed.length === 0) {
          setError("The CSV file appears to be empty.");
          return;
        }
        setRows(parsed);
        setFileName(file.name);
        setExpandedGroups({});
        setJustCreated(new Set());
      } catch {
        setError("Failed to parse the CSV. Please check the file format.");
      }
    };
    reader.readAsText(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  }

  function handleReset() {
    setRows(null);
    setFileName("");
    setError("");
    setExpandedGroups({});
    setJustCreated(new Set());
    if (inputRef.current) inputRef.current.value = "";
  }

  function downloadJSON() {
    const blob = new Blob([JSON.stringify(grouped, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName.replace(/\.csv$/i, "") + "_grouped.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Group expand/collapse ──────────────────────────────────────────────────

  function isExpanded(key) {
    return expandedGroups[key] !== false; // default open
  }

  function toggleGroup(key) {
    setExpandedGroups((prev) => ({ ...prev, [key]: !isExpanded(key) }));
  }

  // ── Journal Entry ──────────────────────────────────────────────────────────

  function openJournalEntry(adpCo, coRows) {
    payloadRef.current = null;
    setSingleJEReady(false);
    setJournalPreview({ adpCo, rows: coRows });
  }

  // Remove all rows belonging to an ADP CO group from persisted state.
  // If no rows remain, resets to null so the upload zone reappears.
  const removeGroupRows = useCallback(
    (adpCo) => {
      setRows((prev) => {
        if (!prev) return prev;
        const next = prev.filter((row) => (row[adpCoKey] ?? "") !== adpCo);
        return next.length > 0 ? next : null;
      });
    },
    [adpCoKey, setRows],
  );

  const confirmCreateJournalEntry = useCallback(async () => {
    const payload = payloadRef.current;
    if (!payload) {
      showError("Payload is not ready — wait for account data to load.");
      return;
    }
    try {
      await createJournalMut.mutateAsync(payload);
      showSuccess(`Journal entry created for ${journalPreview.adpCo}`);
      setJournalPreview(null);
      removeGroupRows(journalPreview.adpCo);
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  }, [createJournalMut, journalPreview, removeGroupRows]);

  // ── Bulk Journal Entry ────────────────────────────────────────────────────

  function openBulkJournalEntry() {
    setBulkPayloads({});
    setBulkResults({});
    setBulkSubmitting(new Set());
    setBulkInProgress(false);
    setBulkOpen(true);
  }

  const confirmBulkJournalEntry = useCallback(async () => {
    if (!grouped) return;
    const allCoKeys = Object.keys(grouped);

    // Enforce: number of journal entries created == number of ADP CO groups.
    // Refuse to submit unless every group has a ready payload (and hasn't
    // already succeeded on a previous attempt).
    const notReady = allCoKeys.filter(
      (adpCo) => bulkResults[adpCo]?.ok !== true && !bulkPayloads[adpCo],
    );
    if (notReady.length > 0) {
      showError(
        `Still waiting on ${notReady.length} ADP CO ${
          notReady.length === 1 ? "group" : "groups"
        } to finish loading: ${notReady.join(", ")}`,
      );
      return;
    }

    // Submit one JE per ADP CO group, skipping ones already succeeded so
    // retries don't double-post.
    const targets = allCoKeys.filter((adpCo) => bulkResults[adpCo]?.ok !== true);
    if (targets.length === 0) {
      showError("No journal entries left to submit.");
      return;
    }

    setBulkInProgress(true);
    let okCount = 0;
    let failCount = 0;

    // Submit sequentially so failures don't get lost in a Promise.all reject.
    for (const adpCo of targets) {
      setBulkSubmitting((prev) => new Set([...prev, adpCo]));
      try {
        await createJournalMut.mutateAsync(bulkPayloads[adpCo]);
        setBulkResults((prev) => ({ ...prev, [adpCo]: { ok: true } }));
        removeGroupRows(adpCo);
        okCount += 1;
      } catch (err) {
        const msg = getApiErrorMessage(err);
        setBulkResults((prev) => ({ ...prev, [adpCo]: { ok: false, error: msg } }));
        failCount += 1;
      } finally {
        setBulkSubmitting((prev) => {
          const next = new Set(prev);
          next.delete(adpCo);
          return next;
        });
      }
    }

    setBulkInProgress(false);

    if (failCount === 0) {
      showSuccess(`All ${okCount} journal ${okCount === 1 ? "entry" : "entries"} created.`);
      setBulkOpen(false);
    } else if (okCount === 0) {
      showError(`All ${failCount} submissions failed. See per-group errors above.`);
    } else {
      showError(`${okCount} succeeded, ${failCount} failed. Review per-group errors and retry.`);
    }
  }, [grouped, bulkPayloads, bulkResults, createJournalMut, removeGroupRows]);

  const bulkTotalCount = grouped ? Object.keys(grouped).length : 0;
  const bulkReadyCount = grouped
    ? Object.keys(grouped).filter(
        (adpCo) => bulkResults[adpCo]?.ok === true || !!bulkPayloads[adpCo],
      ).length
    : 0;
  const bulkAllReady = bulkTotalCount > 0 && bulkReadyCount === bulkTotalCount;
  const bulkRemainingCount = grouped
    ? Object.keys(grouped).filter((adpCo) => bulkResults[adpCo]?.ok !== true).length
    : 0;

  const journalEntries = useMemo(() => {
    const items = journalEntriesQuery.data?.items ?? [];
    const endDate = payPeriod?.endDate;
    if (!endDate) return items;
    const from = new Date(endDate);
    const to = new Date(endDate);
    to.setDate(to.getDate() + 13);
    return items.filter((item) => {
      if (!item.trandate) return false;
      const t = new Date(item.trandate);
      return t >= from && t <= to;
    });
  }, [journalEntriesQuery.data, payPeriod?.endDate]);
  const journalEntriesByDate = useMemo(() => {
    const groupedByPeriod = {};
    for (const entry of journalEntries) {
      const periodKey =
        entry.posting_period_name || entry.trandate || entry.formatted_trandate || "Unknown Period";
      if (!groupedByPeriod[periodKey]) groupedByPeriod[periodKey] = [];
      groupedByPeriod[periodKey].push(entry);
    }

    const toTimestamp = (value) => {
      const ts = Date.parse(value);
      return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
    };

    // Sort groups by the latest transaction date within each period.
    const latestTrandate = (entries) =>
      entries.reduce((max, e) => Math.max(max, toTimestamp(e.trandate)), Number.NEGATIVE_INFINITY);

    return Object.entries(groupedByPeriod).sort(
      ([, entriesA], [, entriesB]) => latestTrandate(entriesB) - latestTrandate(entriesA),
    );
  }, [journalEntries]);
  const journalEntriesLoading = journalEntriesQuery.isLoading && !!month;
  const [activeTab, setActiveTab] = usePersistedState("adpNetsuite:activeTab", "view");

  // Unique subsidiary IDs from the fetched journal entries
  const subsidiaryIds = useMemo(() => {
    const ids = journalEntries.map((e) => e.subsidiary_id).filter((id) => id != null && id !== "");
    return [...new Set(ids.map(Number))].filter(Number.isFinite);
  }, [journalEntries]);

  const practitionerProductionQuery = usePractitionerProduction(
    {
      subsidiaryIds,
      startDate: payPeriod?.startDate,
      endDate: payPeriod?.endDate,
    },
    { enabled: subsidiaryIds.length > 0 && !!payPeriod?.startDate && !!payPeriod?.endDate },
  );

  // subsidiary_id (number) → total_production for quick lookup in the view
  const productionBySubsidiary = useMemo(() => {
    const items = practitionerProductionQuery.data?.items ?? [];
    const map = {};
    for (const item of items) {
      if (item.subsidiary_id != null && item.total_production != null) {
        map[Number(item.subsidiary_id)] = item.total_production;
      }
    }
    return map;
  }, [practitionerProductionQuery.data]);

  // Convert "June 2026 (1)" → "Jun 2026" for NetSuite AccountingPeriod lookup
  const uploadPeriodName = useMemo(() => {
    const label = uploadPeriod?.period;
    if (!label) return "";
    const match = label.match(/^([A-Za-z]+)\s+(\d{4})/);
    if (!match) return "";
    return `${match[1].slice(0, 3)} ${match[2]}`;
  }, [uploadPeriod?.period]);

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="mt-4 space-y-4">
      {/* ── Tabs ── */}
      <div className="flex gap-1 border-b border-palette-slate-divider">
        {[
          { key: "view", label: "View" },
          { key: "upload", label: "Upload" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-1.5 text-sm font-medium transition-colors ${
              activeTab === t.key
                ? "border-palette-indigo-label text-palette-indigo-badge"
                : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── View tab ── */}
      {activeTab === "view" && (
        <JournalEntriesViewCard
          month={month}
          journalEntriesQuery={journalEntriesQuery}
          journalEntriesLoading={journalEntriesLoading}
          journalEntries={journalEntries}
          journalEntriesByDate={journalEntriesByDate}
          viewColumns={viewColumns}
          productionBySubsidiary={productionBySubsidiary}
          productionLoading={practitionerProductionQuery.isLoading}
        />
      )}

      {/* ── Upload tab — period picker (always shown) ── */}
      {activeTab === "upload" && (
        <Card>
          <p className="text-sm font-semibold text-palette-gray-text mb-0.5">Pay Period</p>
          <p className="text-xs text-palette-gray-secondary mb-3">
            Select the period you are creating journal entries for before uploading a CSV.
          </p>
          <PayPeriod onChange={setUploadPeriod} allowEmpty />
        </Card>
      )}

      {/* ── Upload tab — drop zone (gated behind period) ── */}
      {activeTab === "upload" && !rows && (
        <Card>
          {!uploadPeriod?.month ? (
            <div className="mx-auto w-full max-w-xl flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-palette-gray-divider bg-palette-gray-soft p-8 text-center">
              <span className="text-3xl mb-3">📅</span>
              <p className="text-sm font-semibold text-palette-gray-secondary">
                Select a pay period above to get started
              </p>
            </div>
          ) : (
            <>
              <div
                className="mx-auto w-full max-w-xl flex flex-col items-center justify-center border-2 border-dashed border-palette-gray-divider rounded-lg p-5 cursor-pointer hover:border-palette-indigo-label transition-colors"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => inputRef.current?.click()}
              >
                <span className="text-2xl mb-2">📂</span>
                <p className="text-sm font-medium text-palette-gray-primary">
                  Drop a CSV file here, or click to browse
                </p>
                <p className="text-xs text-palette-gray-primary opacity-60 mt-1 text-center">
                  Rows will be grouped by the <strong>ADP CO</strong> column
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              {error && <p className="mt-3 text-sm text-palette-red-label">{error}</p>}
            </>
          )}
        </Card>
      )}

      {/* ── Upload tab results ── */}
      {activeTab === "upload" && rows && grouped && (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[15px]">{fileName}</span>
              <span className="text-[13px] text-palette-gray-primary">
                {rows.length} rows · {Object.keys(grouped).length} ADP CO groups · {columns.length}{" "}
                columns
              </span>
              {uploadPeriod?.period && (
                <span className="inline-flex items-center gap-1 rounded-full bg-palette-indigo-soft px-2.5 py-0.5 text-xs font-semibold text-palette-indigo-heading ring-1 ring-palette-indigo-border">
                  {uploadPeriod.period}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!uploadPeriod?.month && (
                <span className="text-[13px] text-palette-amber-badge font-medium">
                  ⚠ Select a pay period above to enable journal entry creation
                </span>
              )}
              <Button variant="secondary" size="sm" onClick={downloadJSON}>
                ⬇ Download JSON
              </Button>
              <Button variant="secondary" size="sm" onClick={handleReset}>
                Clear and Upload New
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={openBulkJournalEntry}
                disabled={!grouped || Object.keys(grouped).length === 0 || !uploadPeriod?.month}
                title={!uploadPeriod?.month ? "Select a pay period first" : undefined}
              >
                Create Bulk Journal Entry
              </Button>
            </div>
          </div>

          {/* Group cards */}
          {Object.entries(grouped)
            .sort(([a], [b]) => String(a).localeCompare(String(b)))
            .map(([coKey, coRows]) => {
              const done = justCreated.has(coKey);
              return (
                <Card key={coKey}>
                  {/* Card header */}
                  <div className="flex items-center justify-between gap-3">
                    <button
                      className="flex-1 flex items-center gap-2 text-left"
                      onClick={() => toggleGroup(coKey)}
                    >
                      <span className="font-semibold text-[15px]">
                        {adpCoKey}: {coKey || "(blank)"}
                      </span>
                      <span className="text-[13px] text-palette-gray-primary">
                        {coRows.length} row{coRows.length !== 1 ? "s" : ""}
                      </span>
                      {done && (
                        <span className="ml-2 rounded-full bg-palette-emerald-muted text-palette-emerald-hover text-xs px-2 py-0.5 font-semibold">
                          ✓ Submitted
                        </span>
                      )}
                      <span className="ml-auto text-palette-gray-primary">
                        {isExpanded(coKey) ? "▲" : "▼"}
                      </span>
                    </button>

                    {/* Create Journal Entry button */}
                    <Button
                      variant={done ? "secondary" : "primary"}
                      size="sm"
                      onClick={() => openJournalEntry(coKey, coRows)}
                      disabled={!uploadPeriod?.month}
                      title={!uploadPeriod?.month ? "Select a pay period first" : undefined}
                    >
                      {done ? "Re-submit JE" : "Create Journal Entry"}
                    </Button>
                  </div>

                  {/* Expanded row table */}
                  {isExpanded(coKey) && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full table-fixed text-[13px]">
                        <colgroup>
                          {columns.map((col) => (
                            <col
                              key={`upload-col-${col}`}
                              style={{ width: uploadColumnWidths[col] || undefined }}
                            />
                          ))}
                        </colgroup>
                        <thead>
                          <tr className="border-b border-palette-gray-divider">
                            {columns.map((col) => (
                              <th
                                key={col}
                                className="px-3 py-2 text-left font-semibold text-palette-gray-primary whitespace-nowrap"
                                title={col}
                              >
                                <span className="block truncate">{col}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {coRows.map((row, i) => (
                            <tr
                              key={i}
                              className={i % 2 === 0 ? "bg-white" : "bg-palette-gray-badge"}
                            >
                              {columns.map((col) => (
                                <td
                                  key={col}
                                  className="px-3 py-2 whitespace-nowrap text-palette-gray-text"
                                  title={row[col] == null ? "" : String(row[col])}
                                >
                                  <span className="block truncate">{row[col]}</span>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
        </>
      )}

      {/* ── Modals (always mounted regardless of tab) ── */}
      {/* ── Bulk Journal Entry Confirm Modal ── */}
      {bulkOpen && grouped && (
        <ConfirmModal
          isOpen={bulkOpen}
          onClose={() => !bulkInProgress && setBulkOpen(false)}
          onConfirm={confirmBulkJournalEntry}
          title={`Create Bulk NetSuite Journal Entries — ${bulkTotalCount} ADP CO ${
            bulkTotalCount === 1 ? "group" : "groups"
          } (${bulkReadyCount}/${bulkTotalCount} ready)`}
          size="3xl"
          confirmText={
            bulkInProgress
              ? "Sending…"
              : !bulkAllReady
                ? `Waiting for ${bulkTotalCount - bulkReadyCount} of ${bulkTotalCount}…`
                : `Confirm & Send ${bulkRemainingCount} ${
                    bulkRemainingCount === 1 ? "Entry" : "Entries"
                  }`
          }
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={bulkInProgress}
          confirmDisabled={bulkInProgress || !bulkAllReady || bulkRemainingCount === 0}
          message={
            <ADPNetsuiteBulkSubmitFormPreview
              groups={grouped}
              onPayloadsChange={setBulkPayloads}
              results={bulkResults}
              submitting={bulkSubmitting}
              periodName={uploadPeriodName}
              endDate={uploadPeriod?.endDate}
            />
          }
        />
      )}

      {/* ── Journal Entry Confirm Modal ── */}
      {journalPreview && (
        <ConfirmModal
          isOpen={!!journalPreview}
          onClose={() => !createJournalMut.isPending && setJournalPreview(null)}
          onConfirm={confirmCreateJournalEntry}
          title={`Create NetSuite Journal Entry — ${journalPreview.adpCo}`}
          size="3xl"
          confirmText={createJournalMut.isPending ? "Sending…" : "Confirm & Send"}
          confirmVariant="primary"
          cancelText="Cancel"
          isLoading={createJournalMut.isPending}
          confirmDisabled={createJournalMut.isPending || !singleJEReady}
          message={
            <ADPNetsuiteSubmitFormPreview
              preview={journalPreview}
              onPayloadChange={(payload) => {
                payloadRef.current = payload;
                setSingleJEReady(!!payload);
              }}
              periodName={uploadPeriodName}
              endDate={uploadPeriod?.endDate}
            />
          }
        />
      )}
    </div>
  );
}
